import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { FC, useState } from "react";
import { useLocation } from "react-router-dom";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { NestedRoute } from "shared/routes/types";
import { COLOR_SECONDARY_MAIN, COLOR_WHITE } from "shared/theme/colors";
import { ExpandMoreIcon } from "shared/theme/icons/ExpandMoreIcon";
import {
  StyledNavItemIconContainer,
  StyledNavItemTypography,
} from "../../styled";
import RefreshButton from "./RefreshButton";
import { NavSubItemLink } from "./components/NavSubItemLink";
import { StyledCollapserButton, StyledExpandIcon } from "./styled";

type Props = {
  Icon?: FC;
  title: string;
  subItems: NestedRoute[];
  isLoadingRefetch: boolean;
  handleRefetchSybItems?: () => void;
  isExpanded?: boolean;
};

export const NavItemCollapser: FC<Props> = ({
  Icon,
  title,
  subItems,
  isLoadingRefetch,
  handleRefetchSybItems,
  isExpanded,
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  const getGroupNavigationSegments = (path: string): string => {
    const GROUP_ROUTE_SEGMENTS = 2;
    return path
      .split("/")
      .filter(Boolean)
      .slice(0, GROUP_ROUTE_SEGMENTS)
      .join("/");
  };

  const checkActiveLink = (linkPath: string): boolean => {
    const currentBaseRoute = getGroupNavigationSegments(currentPath);
    const linkBaseRoute = getGroupNavigationSegments(linkPath);

    return currentPath === linkPath || currentBaseRoute === linkBaseRoute;
  };

  const hasSomeActiveSubItem = subItems.some(({ pathname }) =>
    checkActiveLink(pathname),
  );

  const hasShownRefetchIcon =
    Boolean(handleRefetchSybItems) && hasSomeActiveSubItem;

  const onRefreshSubItems = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleRefetchSybItems?.();
  };

  const isVisibleMaintenanceModeIcon = subItems.some(
    (item) => item.isMaintenanceMode,
  );

  const renderDot = () => (
    <Box
      width="0.5rem"
      height="0.5rem"
      borderRadius="50%"
      position="absolute"
      top="-2px"
      right="-1px"
      bgcolor={COLOR_SECONDARY_MAIN}
    />
  );

  return (
    <Box>
      <StyledCollapserButton
        active={hasSomeActiveSubItem}
        expanded={isExpanded}
        onClick={() => setOpen((prev) => !prev)}
        disableRipple
      >
        <FlexCenter>
          {Icon && (
            <StyledNavItemIconContainer
              position="relative"
              expanded={isExpanded}
              active={hasSomeActiveSubItem}
            >
              <Icon />
              {!isExpanded && isVisibleMaintenanceModeIcon && renderDot()}
            </StyledNavItemIconContainer>
          )}
          <Collapse
            in={isExpanded}
            timeout="auto"
            unmountOnExit
            orientation="horizontal"
          >
            <StyledNavItemTypography
              active={hasSomeActiveSubItem}
              variant="bodyLMedium"
            >
              {title}
            </StyledNavItemTypography>
          </Collapse>
        </FlexCenter>
        {isExpanded && (
          <FlexCenter columnGap="4px">
            {hasShownRefetchIcon && (
              <RefreshButton
                isLoading={isLoadingRefetch}
                handleClick={onRefreshSubItems}
              />
            )}
            {!open && isVisibleMaintenanceModeIcon && (
              <Box
                width="0.5rem"
                height="0.5rem"
                borderRadius="50%"
                bgcolor={COLOR_SECONDARY_MAIN}
              />
            )}
            <StyledExpandIcon open={open}>
              <ExpandMoreIcon color={COLOR_WHITE} />
            </StyledExpandIcon>
          </FlexCenter>
        )}
      </StyledCollapserButton>
      {isExpanded && (
        <Collapse in={open} timeout="auto">
          {subItems.map(({ title = "", pathname, isMaintenanceMode }) => (
            <NavSubItemLink
              key={pathname}
              title={title}
              pathname={pathname}
              active={checkActiveLink(pathname)}
              isMaintenanceMode={isMaintenanceMode}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};
